import type { DomainGuide, FieldDoc } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

const f = (key: string, kind: FieldDoc['kind'], formula?: string): FieldDoc =>
  formula === undefined ? { key, kind } : { key, kind, formula };

/**
 * Help catalog for the `reports` domain.
 *
 * Routes and permissions mirror `pages/reports/reports-nav.ts`, which is itself
 * asserted against the registered routes — so a page documented here is a page
 * an operator can actually reach.
 *
 * Every sign convention below was re-read against the engine, not inherited:
 * `apps/bet/src/repo/bets.ts` settles a bet as `winloss = payout − stake`
 * (player's view), `apps/commission/src/engine/winloss-split.ts` carries that
 * straight into the member leg and negates it for the house base, and the daily
 * and by-provider screens negate it again for display. That is why the same
 * quantity is `memberWinloss` on one page and `houseWinloss` on the next, with
 * opposite signs.
 */
export const reportsGuide: DomainGuide = {
  domain: 'reports',
  pages: [
    {
      id: 'reports.overview',
      route: '/reports',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('deposit', 'money-unsigned'),
        f('withdraw', 'money-unsigned'),
        f('netFlow', 'money-signed', 'netFlow = deposit − withdraw'),
        f('firstDeposits', 'count'),
        f('turnover', 'money-unsigned'),
        f('houseWinloss', 'money-signed', 'houseWinloss = −(payout − stake)'),
        f('bonusOut', 'money-unsigned'),
        f('activeMembers', 'count'),
        f('registeredMembers', 'count'),
        f('depositingMembers', 'count'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },

    {
      id: 'reports.winloss',
      route: '/reports/winloss',
      permission: 'mlm:node_read',
      status: 'live',
      fields: [
        f('vantage', 'text'),
        f('rowName', 'text'),
        f('position', 'text'),
        f('betCount', 'count'),
        f('turnover', 'money-unsigned'),
        f('memberWinloss', 'money-signed', 'memberWinloss = payout − stake'),
        f('groupMember', 'money-signed'),
        f('groupCompany', 'money-signed', 'company = base × PT'),
        f('groupProvider', 'money-signed', 'provider = base − company'),
        f('commission', 'money-unsigned'),
        f('groupTotal', 'money-signed', 'total = winloss + commission'),
      ],
      cautionCount: 7,
      warningCount: 0,
    },

    {
      id: 'reports.winlossDaily',
      route: '/reports/winloss/daily',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('day', 'text'),
        f('turnover', 'money-unsigned'),
        f('payout', 'money-unsigned'),
        f('houseWinloss', 'money-signed', 'houseWinloss = −(payout − stake)'),
        f('betCount', 'count'),
      ],
      cautionCount: 3,
      warningCount: 0,
    },

    {
      id: 'reports.winlossProviders',
      route: '/reports/winloss/providers',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('provider', 'text'),
        f('turnover', 'money-unsigned'),
        f('payout', 'money-unsigned'),
        f('houseWinloss', 'money-signed', 'houseWinloss = −(payout − stake)'),
        f('betCount', 'count'),
      ],
      cautionCount: 3,
      warningCount: 0,
    },

    {
      id: 'reports.finance',
      route: '/reports/finance',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('financeDay', 'text'),
        f('deposit', 'money-unsigned'),
        f('withdraw', 'money-unsigned'),
        f('netFlow', 'money-signed', 'netFlow = deposit − withdraw'),
        f('depositTxCount', 'count'),
        f('withdrawTxCount', 'count'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },

    {
      // The one screen that shows unsettled volume. `settled` is the field that
      // makes the rest of the suite readable: a withdraw counts from `approved`
      // AND `completed`, because the wallet was debited when the request was
      // made and only a refund puts it back.
      id: 'reports.financeSummary',
      route: '/reports/finance/summary',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('deposit', 'money-unsigned'),
        f('withdraw', 'money-unsigned'),
        f('netFlow', 'money-signed', 'netFlow = deposit − withdraw'),
        f('withdrawNet', 'money-unsigned', 'withdrawNet = withdraw − withdrawFee'),
        f('withdrawFee', 'money-unsigned'),
        f('side', 'text'),
        f('status', 'status'),
        f('settled', 'status'),
        f('amount', 'money-unsigned'),
        f('txCount', 'count'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },

    {
      id: 'reports.financeChannels',
      route: '/reports/finance/channels',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('channel', 'text'),
        f('deposit', 'money-unsigned'),
        f('withdraw', 'money-unsigned'),
        f('netFlow', 'money-signed', 'netFlow = deposit − withdraw'),
        f('txCount', 'count', 'txCount = depositTxCount + withdrawTxCount'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },

    {
      // Attribution here is only as complete as the operator's bookkeeping: a
      // withdraw records its rail at completion, so an approved-but-unpaid
      // payout has none yet and lands in the unattributed row.
      id: 'reports.financeBanks',
      route: '/reports/finance/banks',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('bank', 'text'),
        f('accountNumber', 'text'),
        f('accountName', 'text'),
        f('deposit', 'money-unsigned'),
        f('depositTxCount', 'count'),
        f('withdraw', 'money-unsigned'),
        f('withdrawTxCount', 'count'),
        f('netFlow', 'money-signed', 'netFlow = deposit − withdraw'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },

    {
      // The one report keyed by member rather than by day, channel or account,
      // which is why it is the only one that has to answer for a name and a
      // phone: both follow `fieldsForLevel`, the same ladder the members list
      // applies, and svc-report withholds them below it rather than trusting
      // the screen to.
      id: 'reports.memberStats',
      route: '/reports/members',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('username', 'text'),
        f('name', 'text'),
        f('phone', 'text'),
        f('depositCount', 'count'),
        f('deposit', 'money-unsigned'),
        f('withdrawCount', 'count'),
        f('withdraw', 'money-unsigned'),
        f('bankProfit', 'money-signed', 'bankProfit = deposit − withdraw'),
        f('ranking', 'text'),
      ],
      cautionCount: 5,
      warningCount: 0,
    },

    {
      id: 'reports.firstDepositSummary',
      route: '/reports/first-deposits',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('registrations', 'count'),
        f('depositedMembers', 'count'),
        f('notDepositedMembers', 'count'),
        f('firstDepositTotal', 'money-unsigned'),
        f(
          'firstDepositAverage',
          'money-unsigned',
          'average = firstDepositTotal ÷ depositedMembers',
        ),
      ],
      cautionCount: 6,
      warningCount: 0,
    },

    {
      id: 'reports.firstDepositList',
      route: '/reports/first-deposits/list',
      permission: 'report:read',
      status: 'live',
      fields: [
        f('firstDepositAt', 'date'),
        f('registeredAt', 'date'),
        f('username', 'text'),
        f('name', 'text'),
        f('phone', 'text'),
        f('amount', 'money-unsigned'),
        f('bonus', 'money-unsigned'),
        f('referrerCode', 'text'),
      ],
      cautionCount: 6,
      warningCount: 0,
    },

    {
      // `POST /export-jobs` builds the CSV inline and stores only its row and
      // byte counts. `GET /export-jobs/:id/download` rebuilds it on demand
      // under the caller's own scope, so the file is real — but it is not a
      // snapshot of the moment the job ran, which is said on the page rather
      // than discovered in a spreadsheet. The XLSX option and the three
      // placeholder report codes are gone from the form; a job raised for one
      // of those codes before they were withdrawn is still listed and no
      // longer downloads, which is what the third caution is about.
      id: 'reports.exports',
      route: '/reports/exports',
      permission: 'report:read',
      status: 'partially-wired',
      fields: [
        f('reportCode', 'text'),
        f('exportFormat', 'text'),
        f('exportStatus', 'text'),
        f('requestedAt', 'text'),
        f('completedAt', 'text'),
      ],
      cautionCount: 4,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
